// Inner image carousel inside the Instagram lightbox: one <instagram-lightbox-
// carousel> per post slide, holding stacked .instagram-lightbox__frame
// elements (image + its product pins), dot buttons, and prev/next arrows.
// Outer post navigation (arrows/keys/swipe between posts) belongs to
// <image-lightbox> (component-image-lightbox.js); horizontal swipes inside a
// multi-image post are claimed by this element instead.

if (!customElements.get('instagram-lightbox-carousel')) {
  customElements.define(
    'instagram-lightbox-carousel',
    class InstagramLightboxCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.frames = Array.from(this.querySelectorAll('.instagram-lightbox__frame'));
        this.dots = Array.from(this.querySelectorAll('.instagram-lightbox__frame-dot'));
        this.prevBtn = this.querySelector('.instagram-lightbox__frame-nav--prev');
        this.nextBtn = this.querySelector('.instagram-lightbox__frame-nav--next');
        this.currentIndex = 0;

        if (this.frames.length < 2) return;

        this.prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.show(this.currentIndex - 1);
        });
        this.nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.show(this.currentIndex + 1);
        });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', (e) => {
            e.stopPropagation();
            this.show(parseInt(dot.dataset.frameTarget, 10) || 0);
          });
        });

        // Claim horizontal swipes so the outer lightbox doesn't change posts
        // while the user is browsing this post's images.
        this.touchStartX = 0;
        this.addEventListener(
          'touchstart',
          (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
            e.stopPropagation();
          },
          { passive: true }
        );
        this.addEventListener(
          'touchend',
          (e) => {
            e.stopPropagation();
            const diff = this.touchStartX - e.changedTouches[0].screenX;
            if (Math.abs(diff) > 50) {
              this.show(this.currentIndex + (diff > 0 ? 1 : -1));
            }
          },
          { passive: true }
        );

        this.updateNav();
      }

      show(index) {
        if (index < 0) index = 0;
        if (index >= this.frames.length) index = this.frames.length - 1;
        if (index === this.currentIndex) return;
        this.currentIndex = index;

        this.frames.forEach((frame, i) => {
          frame.classList.toggle('is-active', i === index);
          if (i === index) frame.removeAttribute('aria-hidden');
          else frame.setAttribute('aria-hidden', 'true');
        });
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });

        const img = this.frames[index].querySelector('img');
        if (img) img.loading = 'eager';

        this.updateNav();
      }

      updateNav() {
        if (!this.prevBtn) return;
        this.prevBtn.disabled = this.currentIndex === 0;
        this.nextBtn.disabled = this.currentIndex === this.frames.length - 1;
      }
    }
  );
}

// Video playback for the Instagram lightbox.
//
// The visible video autoplays (muted + playsinline, as browser autoplay
// policies require) and pauses as soon as it stops being the visible frame, so
// only one video ever plays and offscreen ones don't burn CPU. Under
// prefers-reduced-motion nothing autoplays — the poster shows and the user can
// start it themselves. The custom toggle replaces native controls and is the
// WCAG 2.2.2 "pause" mechanism for this looping motion; its accessible name
// tracks the real media state via play/pause events, so it stays correct no
// matter what initiated the change.
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isVisibleFrame = (video) => {
    const frame = video.closest('.instagram-lightbox__frame');
    const slide = video.closest('.image-lightbox__slide');
    const lightbox = video.closest('image-lightbox');
    return !!(
      lightbox &&
      lightbox.hasAttribute('open') &&
      (!slide || slide.classList.contains('active')) &&
      (!frame || frame.classList.contains('is-active'))
    );
  };

  const syncToggle = (video) => {
    const toggle = video.parentElement && video.parentElement.querySelector('[data-video-toggle]');
    if (!toggle) return;
    const playing = !video.paused;
    toggle.classList.toggle('is-playing', playing);
    const key = playing ? 'data-label-pause' : 'data-label-play';
    const label = toggle.getAttribute(key);
    if (label) toggle.setAttribute('aria-label', label);
  };

  const sync = (lightbox) => {
    lightbox.querySelectorAll('video').forEach((video) => {
      if (isVisibleFrame(video)) {
        // `userPaused` keeps an explicit pause sticky while the frame stays up.
        if (video.paused && !video.dataset.userPaused && !reducedMotion.matches) {
          // Autoplay can still be refused (e.g. Low Power Mode) — that's fine,
          // the poster stays and the toggle offers playback.
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => syncToggle(video));
          syncToggle(video);
        }
      } else if (!video.paused) {
        // Note: `userPaused` is deliberately NOT cleared here. WCAG 2.2.2
        // defines "pause" as stopped by user request and not resumed until
        // the user requests it, so an explicit pause has to survive leaving
        // and re-entering the frame. Only the toggle clears it.
        video.pause();
      }
    });
  };

  const init = (lightbox) => {
    if (lightbox.dataset.videoControllerReady) return;
    lightbox.dataset.videoControllerReady = 'true';

    lightbox.querySelectorAll('video').forEach((video) => {
      video.addEventListener('play', () => syncToggle(video));
      video.addEventListener('pause', () => syncToggle(video));
      syncToggle(video);
    });

    lightbox.querySelectorAll('[data-video-toggle]').forEach((toggle) => {
      const video = toggle.parentElement.querySelector('video');
      if (!video) return;
      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (video.paused) {
          delete video.dataset.userPaused;
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => syncToggle(video));
        } else {
          video.dataset.userPaused = 'true';
          video.pause();
        }
        // The play/pause events are authoritative, but update immediately so
        // the label is never stale if an event is delayed or suppressed.
        syncToggle(video);
      });
    });

    // One observer covers every state change that matters: the lightbox
    // opening/closing (`open`) and slide/frame activation (class changes).
    // Debounced on a microtask rather than rAF: rAF is suspended while the
    // document is hidden, which would strand playback state on tab switches.
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      Promise.resolve().then(() => {
        queued = false;
        sync(lightbox);
      });
    });
    observer.observe(lightbox, { attributes: true, attributeFilter: ['open', 'class'], subtree: true });

    reducedMotion.addEventListener('change', () => sync(lightbox));
    sync(lightbox);
  };

  const initAll = () => document.querySelectorAll('image-lightbox.instagram-lightbox').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // ModalDialog relocates the lightbox to <body> on connect, and the theme
  // editor re-renders sections, so pick up late arrivals too.
  document.addEventListener('shopify:section:load', initAll);
})();

// Overflow affordance for the lightbox info panel.
//
// The panel is a scrolling content row above a pinned meta row, so a long
// caption is clipped by the meta rule with nothing to say it continues. When
// (and only when) there is content below the fold, `.is-overflowing` fades the
// bottom of the content — see the mask in section-custom-instagram-posts.css.
// CSS has no overflow query, so the state is computed here.
(() => {
  // Slack for sub-pixel scroll positions; without it a panel scrolled fully to
  // the bottom can report a fractional remainder and keep the fade up.
  const EPSILON = 2;

  const update = (panel) => {
    const hidden = panel.scrollHeight - panel.clientHeight;
    const remaining = hidden - panel.scrollTop;
    panel.classList.toggle('is-overflowing', hidden > EPSILON && remaining > EPSILON);
  };

  const init = (lightbox) => {
    if (lightbox.dataset.overflowFadeReady) return;
    lightbox.dataset.overflowFadeReady = 'true';

    const panels = Array.from(lightbox.querySelectorAll('.instagram-lightbox__info-inner'));
    if (!panels.length) return;

    const updateAll = () => panels.forEach(update);

    panels.forEach((panel) => {
      panel.addEventListener('scroll', () => update(panel), { passive: true });
    });

    // Inactive slides are display:none, so a panel has no honest metrics until
    // its slide is shown — remeasuring has to be driven by the lightbox opening
    // (`open`) and by slide activation (class changes), exactly as the video
    // controller above does. Debounced on a microtask, and deliberately
    // mutation-driven rather than ResizeObserver-driven: observer callbacks
    // ride the rendering steps, which a hidden document suspends.
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      Promise.resolve().then(() => {
        queued = false;
        updateAll();
      });
    });
    observer.observe(lightbox, { attributes: true, attributeFilter: ['open', 'class'], subtree: true });

    // Viewport resize changes the panel height without mutating anything.
    window.addEventListener('resize', updateAll, { passive: true });

    // Product and avatar images settle after layout, changing scrollHeight
    // without any mutation of the panel. `load` doesn't bubble, hence capture.
    lightbox.addEventListener(
      'load',
      (event) => {
        const panel = event.target.closest && event.target.closest('.instagram-lightbox__info-inner');
        if (panel) update(panel);
      },
      true
    );

    updateAll();
  };

  const initAll = () => document.querySelectorAll('image-lightbox.instagram-lightbox').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
